import type { FC } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface NoContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Goes on with no contact — exactly what Confirm does when there are some. */
  onConfirm: () => void;
}

/**
 * Asked when Confirm is pressed with no contact saved: says what the alerts are
 * for once more, and moves on only if the customer confirms they want none.
 */
const NoContactDialog: FC<NoContactDialogProps> = ({ open, onOpenChange, onConfirm }) => {
  const { t } = useTranslation(["occupants"]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-8 rounded-lg p-8 sm:max-w-[560px]">
        <DialogHeader className="text-left">
          <DialogTitle className="pr-6 leading-normal">
            {t("occupants:occupants-no-contact-title", "No contact saved")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <DialogDescription>
            {t(
              "occupants:occupants-no-contact-description",
              "Get an alert by phone, text message or email to let you know when our snow removal crew enters your area for the final cleanup, so you can move your vehicle.",
            )}
          </DialogDescription>
          <p className="text-sm text-muted-foreground">
            {t("occupants:occupants-no-contact-prompt", "Please confirm this action.")}
          </p>
        </div>

        <DialogFooter className="flex-row justify-between gap-3 sm:justify-between">
          <Button className="cursor-pointer" type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("occupants:occupants-cancel", "Cancel")}
          </Button>
          <Button className="cursor-pointer" type="button" onClick={onConfirm}>
            {t("occupants:occupants-no-contact-confirm", "I confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoContactDialog;
